import SideBarContainer from 'Containers/SideBarContainer';
import ReportsLayout from 'Pages/reports/components/ReportsLayout/ReportsLayout';
import ReportsDataGrid from 'Pages/reports/components/ReportsDataGrid/ReportsDataGrid';
import EmployeesReportFilters from './components/EmployeesReportFilters/EmployeesReportFilters';
import useEmployeeReportPage from './hooks/useEmployeeReportPage';
import { useHistory } from 'react-router-dom';
import { Padding } from 'Components/Padding';
import ProgressIndicator from 'Components/ProgressIndicator';
import ReportsTitle from 'Pages/reports/components/ReportsTitle/ReportsTitle';
import { buildTime } from 'Pages/reports/utils/buildTime';
import { formatDate } from 'Utils/format-date';
import useReportButtons from 'Pages/reports/hooks/useReportButtons';
import { ReportsButtonGroup } from 'Pages/reports/components/ReportsButtonGroup/ReportsButtonGroup';
import type { GridColDef } from '@mui/x-data-grid';

interface Row {
  id: string;
  payrollNum: number | string;
  fullName: string;
  terminalName: string;
  jobTitleName: string;
  age: number | string;
  entryDate: string;
  completedCourses: number;
  totalSubscribedCourses: number;
  completedTrainingHours: number;
  totalTrainingHours: number;
  sessions: Date | null;
}

export default function Page() {
  const {
    employeesReportData,
    employeesReportLoading,
    employeesReportError,
    pagination,
    t2,
    handleSearch,
    handlePageChange,
    handleRowsPerPageChange,
    handleExport,
  } = useEmployeeReportPage();

  const history = useHistory();

  const buttons = useReportButtons();

  const columns: GridColDef[] = [
    {
      field: 'payrollNum',
      headerName: t2('payroll_number'),
      flex: 0.9,
      minWidth: 110,
      align: 'center',
      headerAlign: 'center',
    },
    { field: 'fullName', headerName: t2('full_name'), flex: 2, minWidth: 200 },
    { field: 'terminalName', headerName: t2('terminal'), flex: 1.2, minWidth: 140 },
    { field: 'jobTitleName', headerName: t2('job_title'), flex: 1.2, minWidth: 140 },
    {
      field: 'age',
      headerName: t2('age'),
      flex: 0.5,
      minWidth: 70,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'entryDate',
      headerName: t2('entry_date'),
      flex: 0.9,
      minWidth: 110,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'coursesTaken',
      headerName: t2('courses_taken'),
      flex: 1.1,
      minWidth: 130,
      align: 'center',
      headerAlign: 'center',
      renderCell: params => (
        <ProgressIndicator completed={params.row.completedCourses} total={params.row.totalSubscribedCourses} />
      ),
    },
    {
      field: 'pendingCourses',
      headerName: t2('pending_courses'),
      flex: 0.8,
      minWidth: 110,
      align: 'center',
      headerAlign: 'center',
      valueGetter: params => `${params.row.totalSubscribedCourses - params.row.completedCourses}`,
    },
    {
      field: 'completedTrainingHours',
      headerName: t2('completed_training_hours'),
      flex: 1.3,
      minWidth: 160,
      align: 'center',
      headerAlign: 'center',
      renderCell: params =>
        `${buildTime(params.row.completedTrainingHours, t2)} / ${buildTime(params.row.totalTrainingHours, t2)}`,
    },
    {
      field: 'sessionsNum',
      headerName: t2('sessions_number'),
      flex: 0.9,
      minWidth: 110,
      align: 'center',
      headerAlign: 'center',
      valueGetter: params => (params.row.sessions != null ? formatDate(String(params.row.sessions)) : '-'),
    },
  ];

  const rows: Row[] = employeesReportData.map(employee => ({
    id: employee.id,
    payrollNum: employee.payrollNum,
    fullName: employee.fullName,
    terminalName: employee.terminalName,
    jobTitleName: employee.jobTitleName,
    age: employee.age,
    entryDate: employee.entryDate,
    completedCourses: employee.completedCourses,
    totalSubscribedCourses: employee.totalSubscribedCourses,
    completedTrainingHours: employee.completedTrainingHours,
    totalTrainingHours: employee.totalTrainingHours,
    sessions: employee.sessions,
  }));

  return (
    <SideBarContainer>
      <ReportsLayout
        title={<ReportsTitle title={t2('title')} accent={t2('title_accent')} />}
        buttons={<ReportsButtonGroup buttons={buttons} />}
        onExport={handleExport}
      />
      <Padding padding="1rem">
        <EmployeesReportFilters onSearch={handleSearch} />
        <Padding padding="0.5rem 0" />
        <ReportsDataGrid<Row>
          columns={columns}
          rows={rows}
          loading={employeesReportLoading}
          error={employeesReportError}
          pagination={pagination}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          onRowClick={row => history.push(`/reporte/empleados/${row.id}`)}
        />
      </Padding>
    </SideBarContainer>
  );
}
